import React, { useState, useEffect } from 'react';
import { creditsAPI } from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { 
  Coins, 
  Sparkles, 
  Check, 
  Phone, 
  CreditCard,
  Loader2,
  Star,
  Zap,
  Crown
} from 'lucide-react';
import { toast } from 'sonner';

const packIcons = {
  pack_basic: Coins,
  pack_standard: Star,
  pack_premium: Zap,
  pack_pro: Crown,
};

const CreditsModal = ({ open, onOpenChange, onPurchaseComplete }) => {
  const [packs, setPacks] = useState([]);
  const [balance, setBalance] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedPack, setSelectedPack] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('mtn_momo');
  const [purchasing, setPurchasing] = useState(false);
  const [step, setStep] = useState('select'); // 'select', 'payment', 'confirming'

  useEffect(() => {
    if (open) {
      loadData();
    }
  }, [open]);

  const loadData = async () => {
    try {
      const [balanceRes, packsRes] = await Promise.all([
        creditsAPI.getBalance(),
        creditsAPI.getPacks()
      ]);
      setBalance(balanceRes.data.balance);
      setPacks(packsRes.data.packs);
    } catch (error) {
      console.error('Error loading credits data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPack = (pack) => {
    setSelectedPack(pack);
    setStep('payment');
  };

  const handlePurchase = async () => {
    if (!phoneNumber || phoneNumber.length < 9) {
      toast.error('Veuillez entrer un numéro de téléphone valide');
      return;
    }

    setPurchasing(true);
    try {
      const response = await creditsAPI.initiatePurchase(
        selectedPack.id,
        phoneNumber,
        paymentMethod
      );
      
      toast.success('Demande de paiement envoyée !');
      setStep('confirming');
      
      // For demo: auto-confirm after 3 seconds
      setTimeout(async () => {
        try {
          await creditsAPI.confirmPurchase(response.data.transaction_id);
          toast.success(`${selectedPack.credits} crédits ajoutés à votre compte !`);
          loadData();
          setStep('select');
          setSelectedPack(null);
          setPhoneNumber('');
          if (onPurchaseComplete) {
            onPurchaseComplete();
          }
        } catch (e) {
          console.error('Confirmation error:', e);
          toast.error('Erreur lors de la confirmation du paiement');
        } finally {
          setPurchasing(false);
        }
      }, 3000);
      
    } catch (error) {
      toast.error('Erreur lors du paiement');
      console.error(error);
      setPurchasing(false);
    }
  };

  if (loading) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-lg">
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-[#E53935]" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#E53935]" />
            Crédits IA
          </DialogTitle>
          <DialogDescription>
            Rechargez vos crédits pour utiliser l&apos;assistant IA
          </DialogDescription>
        </DialogHeader>

        {/* Current Balance */}
        <div className="bg-gradient-to-r from-[#1A237E] to-[#3949AB] rounded-xl p-4 text-white">
          <p className="text-sm text-white/70">Solde actuel</p>
          <div className="flex items-center gap-2">
            <Coins className="w-6 h-6" />
            <span className="text-3xl font-bold">{balance}</span>
            <span className="text-white/70">crédits</span>
          </div>
        </div>

        {step === 'select' && (
          <>
            <p className="text-sm text-slate-600 mt-4">Choisissez un pack :</p>
            <div className="space-y-3">
              {packs.map((pack) => {
                const Icon = packIcons[pack.id] || Coins;
                return (
                  <Card 
                    key={pack.id}
                    className={`cursor-pointer transition-all hover:border-[#E53935] ${
                      pack.popular ? 'border-[#E53935] border-2 relative' : ''
                    }`}
                    onClick={() => handleSelectPack(pack)}
                  >
                    {pack.popular && (
                      <Badge className="absolute -top-2 right-4 bg-[#E53935]">
                        Populaire
                      </Badge>
                    )}
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            pack.popular ? 'bg-[#FFEBEE]' : 'bg-slate-100'
                          }`}>
                            <Icon className={`w-5 h-5 ${
                              pack.popular ? 'text-[#E53935]' : 'text-slate-600'
                            }`} />
                          </div>
                          <div>
                            <h4 className="font-semibold text-slate-900">{pack.name}</h4>
                            <p className="text-sm text-slate-500">{pack.description}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-[#1A237E]">
                            {pack.price.toLocaleString()} FCFA
                          </p>
                          <p className="text-xs text-slate-500">
                            {pack.credits} crédits
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </>
        )}

        {step === 'payment' && selectedPack && (
          <div className="space-y-4">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => { setStep('select'); setSelectedPack(null); }}
              className="mb-2"
            >
              ← Retour aux packs
            </Button>

            <Card className="bg-slate-50">
              <CardContent className="p-4">
                <div className="flex justify-between items-center">
                  <span className="font-medium">{selectedPack.name}</span>
                  <span className="font-bold text-[#1A237E]">
                    {selectedPack.price.toLocaleString()} FCFA
                  </span>
                </div>
              </CardContent>
            </Card>

            <div className="space-y-3">
              <Label>Méthode de paiement</Label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setPaymentMethod('mtn_momo')}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    paymentMethod === 'mtn_momo'
                      ? 'border-yellow-500 bg-yellow-50'
                      : 'border-slate-200'
                  }`}
                >
                  <div className="text-center">
                    <span className="text-2xl">📱</span>
                    <p className="font-medium text-sm mt-1">MTN MoMo</p>
                  </div>
                </button>
                <button
                  onClick={() => setPaymentMethod('orange_money')}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    paymentMethod === 'orange_money'
                      ? 'border-orange-500 bg-orange-50'
                      : 'border-slate-200'
                  }`}
                >
                  <div className="text-center">
                    <span className="text-2xl">📲</span>
                    <p className="font-medium text-sm mt-1">Orange Money</p>
                  </div>
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Numéro de téléphone</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  placeholder="6XX XXX XXX"
                  className="pl-10"
                />
              </div>
              <p className="text-xs text-slate-500">
                Vous recevrez une demande de paiement sur ce numéro
              </p>
            </div>

            <Button
              onClick={handlePurchase}
              disabled={purchasing || !phoneNumber}
              className="w-full bg-[#E53935] hover:bg-[#C62828]"
            >
              {purchasing ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <CreditCard className="w-4 h-4 mr-2" />
                  Payer {selectedPack.price.toLocaleString()} FCFA
                </>
              )}
            </Button>
          </div>
        )}

        {step === 'confirming' && (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Loader2 className="w-8 h-8 text-yellow-600 animate-spin" />
            </div>
            <h3 className="font-semibold text-lg mb-2">Paiement en cours...</h3>
            <p className="text-slate-500 text-sm">
              Confirmez le paiement sur votre téléphone
            </p>
            <p className="text-xs text-slate-400 mt-4">
              (Demo: Confirmation automatique dans quelques secondes)
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CreditsModal;
